import { axiosInstanceWebsite } from "../instance";

export interface MetaIntegration {
  id: number;
  owner_id: string;
  is_active: boolean;
  ad_account_id: string | null;
  business_id: string | null;
  business_name: string | null;
  expires_at: number | null;
  created_at: string;
  updated_at: string;
}

export interface MetaOAuthUrlResponse {
  authorizationUrl: string;
}

export interface MetaAdAccount {
  id: string;
  name: string;
  account_status: number;
  currency?: string;
  timezone_name?: string;
}

export interface MetaAdAccountsResponse {
  ok: boolean;
  adaccounts: MetaAdAccount[];
}

export interface MetaBusiness {
  id: string;
  name: string;
}

export interface MetaBusinessesResponse {
  ok: boolean;
  businesses: MetaBusiness[];
}

export interface CampaignMetricsResponse {
  ok: boolean;
  ad_account_id: string;
  date_preset: string;
  active_ads_count: number;
  running_campaigns: string[];
  reach: number;
  frequency: number;
  impressions: number;
  video_3s: number;
  video_50: number;
  link_clicks: number;
}

export interface AdTableRow {
  ad_id: string;
  ad_name: string;
  campaign_name: string;
  effective_status: string;
  impressions: number;
  reach: number;
  frequency: number;
  spend: number;
  cpm: number;
  cpc: number;
  ctr: number;
  results: number;
  cost_per_result: number;
  purchases: number;
  roas: number;
  hook_rate: number;
  thruplay: number;
  budget: number | null;
}

export interface AdsTableResponse {
  ok: boolean;
  ad_account_id: string;
  date_start: string;
  date_end: string;
  ads: AdTableRow[];
}

export const getMetaIntegration = async (): Promise<MetaIntegration | null> => {
  const response = await axiosInstanceWebsite.get("/meta/integration");
  return response.data;
};

export const getMetaOAuthUrl = async (): Promise<MetaOAuthUrlResponse> => {
  const response = await axiosInstanceWebsite.get("/meta/oauth/url");
  return response.data;
};

export const getMetaAdAccounts = async (businessId?: string): Promise<MetaAdAccountsResponse> => {
  const response = await axiosInstanceWebsite.get("/meta/ad-accounts", {
    params: businessId ? { business_id: businessId } : undefined,
  });
  return response.data;
};

export const getMetaBusinesses = async (): Promise<MetaBusinessesResponse> => {
  const response = await axiosInstanceWebsite.get("/meta/businesses");
  return response.data;
};

export const updateMetaAdAccount = async (adAccountId: string): Promise<MetaIntegration> => {
  const response = await axiosInstanceWebsite.put("/meta/integration/ad-account", {
    ad_account_id: adAccountId,
  });
  return response.data;
};

export const getCampaignMetrics = async (
  adAccountId: string,
  dateStart?: string,
  dateEnd?: string,
  datePreset?: string
): Promise<CampaignMetricsResponse> => {
  const params: Record<string, string> = {};
  if (dateStart && dateEnd) {
    params.date_start = dateStart;
    params.date_end = dateEnd;
  } else if (datePreset) {
    params.date_preset = datePreset;
  } else {
    params.date_preset = "last_30d";
  }
  const response = await axiosInstanceWebsite.get(
    `/meta/campaigns/${adAccountId}/metrics`,
    { params }
  );
  return response.data;
};

export const getAdsTable = async (
  adAccountId: string,
  dateStart: string,
  dateEnd: string
): Promise<AdsTableResponse> => {
  const response = await axiosInstanceWebsite.get(
    `/meta/campaigns/${adAccountId}/ads`,
    { params: { date_start: dateStart, date_end: dateEnd } }
  );
  return response.data;
};

export const disconnectMetaIntegration = async (): Promise<MetaIntegration> => {
  const response = await axiosInstanceWebsite.post("/meta/integration", {
    isActive: false,
  });
  return response.data;
};
